// Dynamic pastel color generation with guaranteed distinctness
// This system ensures no similar colors are generated, regardless of quantity

interface GeneratedColor {
  hsl: string;
  hue: number;
  saturation: number;
  lightness: number;
  cityName: string;
}

// Track generated colors to ensure uniqueness
const generatedColors: Map<string, GeneratedColor> = new Map();

// Configuration for pastel colors
const PASTEL_CONFIG = {
  saturation: 55,    // Lower saturation for better pastel effect
  lightness: 78,     // Slightly lower lightness for better visibility
  minHueDifference: 45  // Increased minimum difference for more distinct colors
};

// Predefined optimal hues for maximum distinctness
const OPTIMAL_HUES = [
  0,    // Red
  30,   // Orange
  60,   // Yellow
  90,   // Yellow-Green
  120,  // Green
  150,  // Blue-Green
  180,  // Cyan
  210,  // Blue
  240,  // Blue-Purple
  270,  // Purple
  300,  // Magenta
  330   // Pink
];

/**
 * Calculate the minimum hue difference between a candidate hue and existing hues
 */
const getMinHueDifference = (candidateHue: number, existingHues: number[]): number => {
  if (existingHues.length === 0) return 360;
  
  let minDiff = 360;
  
  for (const existingHue of existingHues) {
    // Calculate the shortest distance between hues on the color wheel
    const diff1 = Math.abs(candidateHue - existingHue);
    const diff2 = 360 - diff1;
    const shortestDiff = Math.min(diff1, diff2);
    
    minDiff = Math.min(minDiff, shortestDiff);
  }
  
  return minDiff;
};

/**
 * Find the optimal hue that maximizes distance from existing colors
 */
const findOptimalHue = (existingHues: number[]): number => {
  if (existingHues.length === 0) return OPTIMAL_HUES[0];
  
  let bestHue = 0;
  let maxMinDistance = 0;
  
  // First, try the predefined optimal hues
  for (const candidateHue of OPTIMAL_HUES) {
    const minDistance = getMinHueDifference(candidateHue, existingHues);
    
    if (minDistance > maxMinDistance) {
      maxMinDistance = minDistance;
      bestHue = candidateHue;
    }
  }
  
  // If we found a good hue from the optimal set, use it
  if (maxMinDistance >= PASTEL_CONFIG.minHueDifference) {
    return bestHue;
  }
  
  // If we need more colors, check all hues at 10-degree intervals
  for (let candidateHue = 0; candidateHue < 360; candidateHue += 10) {
    const minDistance = getMinHueDifference(candidateHue, existingHues);
    
    if (minDistance > maxMinDistance) {
      maxMinDistance = minDistance;
      bestHue = candidateHue;
    }
  }
  
  // If we still can't find a good hue, use golden ratio distribution
  if (maxMinDistance < PASTEL_CONFIG.minHueDifference) {
    const goldenRatio = (1 + Math.sqrt(5)) / 2;
    const goldenAngle = 360 / goldenRatio;
    bestHue = (existingHues.length * goldenAngle) % 360;
  }
  
  return bestHue;
};

/**
 * Generate a hash from a string to create a stable seed
 */
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

/**
 * Generate a distinct pastel color for a city name
 * Colors are consistent for the same city name and guaranteed to be distinct
 */
export const generateDistinctPastelColor = (cityName: string, allCityNames: string[] = []): string => {
  // Clean the city name to ensure consistency
  const cleanCityName = cityName.split(',')[0].trim();
  
  // If we already have this color generated, return it
  if (generatedColors.has(cleanCityName)) {
    return generatedColors.get(cleanCityName)!.hsl;
  }
  
  // Get existing hues to avoid conflicts
  const existingHues = Array.from(generatedColors.values()).map(color => color.hue);
  
  // Find the optimal hue that maximizes distance from existing colors
  let optimalHue = findOptimalHue(existingHues);
  
  // Add some variation based on city name for consistency
  const seed = hashString(cleanCityName);
  const seedOffset = (seed % 20) - 10; // Small variation (-10 to +10 degrees)
  optimalHue = (optimalHue + seedOffset + 360) % 360;
  
  // Ensure the final hue still maintains minimum distance
  const finalMinDistance = getMinHueDifference(optimalHue, existingHues);
  if (finalMinDistance < PASTEL_CONFIG.minHueDifference && existingHues.length > 0) {
    // Fall back to pure optimal hue if variation creates conflicts
    optimalHue = findOptimalHue(existingHues);
  }
  
  // Create the new color
  const newColor: GeneratedColor = {
    hue: optimalHue,
    saturation: PASTEL_CONFIG.saturation,
    lightness: PASTEL_CONFIG.lightness,
    hsl: `hsl(${Math.round(optimalHue)}, ${PASTEL_CONFIG.saturation}%, ${PASTEL_CONFIG.lightness}%)`,
    cityName: cleanCityName
  };
  
  // Store the generated color
  generatedColors.set(cleanCityName, newColor);
  
  return newColor.hsl;
};

/**
 * Generate distinct pastel colors for multiple cities at once
 * This ensures optimal distribution across all cities
 */
export const generateDistinctPastelColors = (cityNames: string[]): { [cityName: string]: string } => {
  const result: { [cityName: string]: string } = {};
  const cleanCityNames = cityNames.map(name => name.split(',')[0].trim());
  
  // Clear existing colors to ensure fresh generation
  resetColorCache();
  
  // Generate colors for all cities in order
  cleanCityNames.forEach(cityName => {
    result[cityName] = generateDistinctPastelColor(cityName, cleanCityNames);
  });
  
  return result;
};

/**
 * Reset the color generation cache (useful for testing or when starting fresh)
 */
export const resetColorCache = (): void => {
  generatedColors.clear();
};

/**
 * Get all currently generated colors (useful for debugging)
 */
export const getGeneratedColors = (): GeneratedColor[] => {
  return Array.from(generatedColors.values());
};

/**
 * Remove a city from the color cache
 */
export const removeColorForCity = (cityName: string): void => {
  const cleanCityName = cityName.split(',')[0].trim();
  generatedColors.delete(cleanCityName);
};

/**
 * Regenerate colors for existing cities to ensure they use the improved system
 */
export const regenerateColorsForCities = (cityNames: string[]): { [cityName: string]: string } => {
  // Clear the cache to start fresh
  resetColorCache();
  
  // Generate new colors for all cities
  return generateDistinctPastelColors(cityNames);
};

/**
 * Update city colors array format for TripContext
 */
export const updateCityColorsArray = (cityNames: string[]): Array<{ city: string; color: string }> => {
  const colorMap = regenerateColorsForCities(cityNames);
  
  return cityNames.map(cityName => ({
    city: cityName.split(',')[0].trim(),
    color: colorMap[cityName.split(',')[0].trim()]
  }));
};

// Convert HSL color to hex (needed for some components that require hex)
export const hslToHex = (h: number, s: number, l: number): string => {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/**
 * Convert HSL string to individual H, S, L values
 */
export const parseHSL = (hslString: string): { h: number; s: number; l: number } => {
  const match = hslString.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
  if (!match) {
    throw new Error(`Invalid HSL string: ${hslString}`);
  }
  
  return {
    h: parseInt(match[1], 10),
    s: parseInt(match[2], 10),
    l: parseInt(match[3], 10)
  };
};

/**
 * Convert HSL string to hex
 */
export const hslStringToHex = (hslString: string): string => {
  const { h, s, l } = parseHSL(hslString);
  return hslToHex(h, s, l);
};

// Legacy function for backward compatibility - now uses city name instead of index
export const generateDistinctPastelColorLegacy = (index: number, total: number): string => {
  // Generate a consistent city name based on index for backward compatibility
  const cityName = `City${index}`;
  return generateDistinctPastelColor(cityName);
}; 